<template>
  <div class="select-wrapper">
    <div
      :class="selectClasses"
      @click="toggleDropdown"
      :tabindex="isDisabled ? -1 : 0"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
      @keydown.down.prevent="navigateOptions(1)"
      @keydown.up.prevent="navigateOptions(-1)"
    >
      <input
        v-if="type === 'autocomplete'"
        ref="autocompleteInput"
        :value="searchQuery"
        type="text"
        :placeholder="!value ? placeholder : ''"
        class="select__input"
        @click.stop="openDropdown"
        @input="handleSearch"
        @keydown.enter.prevent="confirmSelection"
        @keydown.space.stop
        @blur="handleBlur"
      />
      <span v-else class="select__value" :class="{ 'select__value--placeholder': !displayValue }">
        {{ displayValue || placeholder }}
      </span>
      <SpinnerLoader
        v-if="isLoading"
        class="select__loader"
      />
      <CloseIcon
        v-else-if="clearable && value != null"
        class="select__clear-icon"
        color="#717680"
        @click.native.stop="clearValue"
      />
      <OpenDownIcon
        v-else
        class="select__icon"
        :color="iconColor"
      />
    </div>

    <transition name="dropdown">
      <div
        v-if="isOpen && !(hideDropdownWhenEmpty && !searchQuery && filteredOptions.length === 0)"
        :class="clsx('select__dropdown', `select__dropdown--${dropdownDir}`)"
      >
        <div
          v-for="(option, index) in filteredOptions"
          :key="getOptionValue(option)"
          class="select__option"
          :class="{
            'is-selected': isSelected(option),
            'is-focused': index === focusedIndex
          }"
          @click="selectOption(getOptionValue(option))"
          @mouseenter="focusedIndex = index"
        >
          <slot name="option" :option="option">
            {{ getOptionLabel(option) }}
          </slot>
        </div>
        <div v-if="filteredOptions.length === 0" class="select__empty">
          {{ emptyText }}
        </div>
      </div>
    </transition>

    <div v-if="hasError" class="select--error">
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { clsx } from 'clsx';
import { EventBus } from '@/shared/lib/event-bus';
import { CloseIcon, OpenDownIcon } from '@/shared/ui/icons';
import { SpinnerLoader } from '@/shared/ui/loader';

const MINIMAL_DROPDOWN_LIST_HEIGHT = 220;

export default {
  components: { CloseIcon, OpenDownIcon, SpinnerLoader },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null
    },
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Select option'
    },
    type: {
      type: String,
      enum: ['select', 'autocomplete'],
      default: 'select',
    },
    searchThreshold: {
      type: Number,
      default: 0
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    iconColor: {
      type: String,
      default: '#09090B',
    },
    filterMode: {
      type: String,
      default: 'local',
      validator: (value) => ['local', 'remote'].includes(value),
    },
    hideDropdownWhenEmpty: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'No options available'
    },
    dropdownPosition: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'top', 'bottom'].includes(value)
    },
    selectFirstByDefault: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      searchQuery: '',
      dropdownDir: 'bottom',
      isUserTyping: false,
    };
  },
  mounted() {
    if (this.type === 'autocomplete' && this.value && !this.isOpen) {
      const selected = this.options.find(opt =>
        this.getOptionValue(opt) === this.getOptionValue(this.value)
      );
      this.searchQuery = selected ? this.getOptionLabel(selected) : '';
    }

    if (!this.value && this.options.length && this.selectFirstByDefault) {
      this.selectOption(this.getOptionValue(this.options[0]));
    }

    document.addEventListener('click', this.handleClickOutside);
    EventBus.$on('selector:opened', this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    EventBus.$off('selector:opened', this.closeDropdown);
  },
  computed: {
    selectClasses() {
      return clsx('select', {
        'select--disabled': this.isDisabled,
        'select--has-error': this.hasError,
        'select--open': this.isOpen && !(this.hideDropdownWhenEmpty && !this.searchQuery && this.filteredOptions.length === 0),
        'select--autocomplete': this.type === 'autocomplete'
      });
    },
    displayValue() {
      if (!this.value) return '';
      const selected = this.options.find(opt => this.getOptionValue(opt) === this.getOptionValue(this.value));
      return selected ? this.getOptionLabel(selected) : '';
    },
    hasError() {
      return this.errorMessages.length > 0;
    },
    filteredOptions() {
      const safeOptions = this.options.filter(opt => opt != null);
      if (this.type !== 'autocomplete' || this.filterMode === 'remote') return safeOptions;
      const isSelectedValueDisplayed = this.value && (this.searchQuery === this.displayValue);
      if (this.searchQuery && !isSelectedValueDisplayed) {
        const query = this.searchQuery.toLowerCase();
        return safeOptions.filter(option => {
          const label = this.getOptionLabel(option).toLowerCase();
          return label.includes(query);
        });
      }
      return safeOptions;
    },
  },
  methods: {
    clsx,
    scrollToSelected() {
      const selectedOption = this.$el.querySelector('.select__option.is-selected');
      const dropdown = this.$el.querySelector('.select__dropdown');
      if (selectedOption && dropdown) {
        const selectedTop = selectedOption.offsetTop;
        const selectedHeight = selectedOption.offsetHeight;
        const dropdownHeight = dropdown.clientHeight;
        dropdown.scrollTop = selectedTop - dropdownHeight / 2 + selectedHeight / 2;
      }
    },
    setDropdownPosition() {
      const wrapper = this.$el;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();

      if (this.dropdownPosition !== 'auto') {
        this.dropdownDir = this.dropdownPosition;
      } else {
        const spaceBelow = window.innerHeight - rect.bottom;
        this.dropdownDir = spaceBelow >= MINIMAL_DROPDOWN_LIST_HEIGHT || spaceBelow >= rect.top ? 'bottom' : 'top';
      }
    },
    toggleDropdown() {
      if (this.isDisabled) return;
      this.isOpen ? this.closeDropdown() : this.openDropdown();
    },
    openDropdown() {
      if (this.isDisabled) return;
      EventBus.$emit('selector:opened');

      this.$nextTick(() => {
        this.setDropdownPosition();
        this.isOpen = true;
        this.focusedIndex = this.filteredOptions.findIndex(opt =>
          this.isSelected(opt)
        );

        if (this.type === 'autocomplete') {
          this.$refs.autocompleteInput?.focus();
        }

        this.$nextTick(() => {
          this.scrollToSelected();
        });
      });
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
      if (this.type === 'autocomplete' && !this.isUserTyping) {
        const selected = this.options.find(
          opt => this.getOptionValue(opt) === this.getOptionValue(this.value)
        );
        this.searchQuery = selected ? this.getOptionLabel(selected) : '';
      }
    },
    selectOption(option) {
      this.$emit('beforeChange', option, this.value);
      this.$emit('input', option);
      this.$emit('change', option);
      this.isUserTyping = false;
      if (this.type === 'autocomplete') {
        const selected = this.options.find(opt => this.getOptionValue(opt) === option);
        this.searchQuery = selected ? this.getOptionLabel(selected) : '';
      }
      this.closeDropdown();
    },
    clearValue() {
      this.isUserTyping = false;
      this.$emit('input', null);
      this.$emit('change', null);
      this.searchQuery = '';
      this.closeDropdown();
    },
    getOptionValue(option) {
      if (option == null) return null;
      return typeof option === 'object' ? option[this.valueKey] : option;
    },
    getOptionLabel(option) {
      if (option == null) return '';
      return typeof option === 'object' ? option[this.labelKey] : option;
    },
    isSelected(option) {
      if (!this.value) return false;
      return this.getOptionValue(option) === this.getOptionValue(this.value);
    },
    navigateOptions(direction) {
      if (!this.isOpen) {
        this.openDropdown();
        return;
      }
      const len = this.filteredOptions.length;
      const newIndex = this.focusedIndex + direction;
      if (newIndex >= 0 && newIndex < len) {
        this.focusedIndex = newIndex;
      }
      this.$nextTick(() => this.scrollToSelected());
    },
    handleSearch() {
      this.searchQuery = this.$refs.autocompleteInput.value;
      this.focusedIndex = -1;
      this.isUserTyping = true;

      if (this.filterMode === 'remote') {
        this.isOpen = false;
      } else {
        if (!this.isOpen) {
          this.openDropdown();
        }
      }

      this.$emit('search', this.searchQuery);
    },
    confirmSelection() {
      if (this.isOpen && this.focusedIndex >= 0 && this.focusedIndex < this.filteredOptions.length) {
        this.selectOption(this.getOptionValue(this.filteredOptions[this.focusedIndex]));
      } else if (this.isOpen && this.filteredOptions.length === 1) {
        this.selectOption(this.getOptionValue(this.filteredOptions[0]));
      }
    },
    handleBlur() {
      setTimeout(() => {
        if (this.isUserTyping) {
          this.isUserTyping = false;
          if (this.value) {
            const selected = this.options.find(
              opt => this.getOptionValue(opt) === this.getOptionValue(this.value)
            );
            this.searchQuery = selected ? this.getOptionLabel(selected) : '';
          } else {
            this.searchQuery = '';
          }
        }
      }, 200);
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) this.closeDropdown();
    },
  },
  watch: {
    value(newVal) {
      if (this.type === 'autocomplete') {
        if (this.isUserTyping) return;
        if (newVal) {
          const selected = this.options.find(
            opt => this.getOptionValue(opt) === this.getOptionValue(newVal)
          );
          this.searchQuery = selected ? this.getOptionLabel(selected) : '';
        } else {
          this.searchQuery = '';
        }
      }
    },
    options(newVal) {
      if (this.type === 'autocomplete') {
        if (this.filterMode === 'remote' && this.searchQuery) {
          if (newVal && newVal.length > 0) {
            this.openDropdown();
          }
        }
        return;
      }

      const selected = this.options.find(
        opt => this.getOptionValue(opt) === this.getOptionValue(this.value)
      );

      if (!this.value && !selected && newVal?.length > 0 && this.selectFirstByDefault) {
        this.selectOption(this.getOptionValue(newVal[0]));
      }
    },
    isLoading(newVal, oldVal) {
      if (this.type === 'autocomplete' && this.filterMode === 'remote') {
        if (newVal === false && oldVal === true && this.searchQuery && !this.options.length) {
          this.openDropdown();
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.select-wrapper {
  position: relative;
  width: 100%;
}

.select {
    position: relative;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    padding: 8px 12px;
    min-height: 39px;
    width: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    overflow: hidden;

    &--disabled {

    background-color: #F6F6F9;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--has-error {
    border-color: #f00;
  }

  &__input {
    font-family: inherit;
    font-size: 14px;
    color: #09090B;
    background: transparent;
    border: none !important;
    outline: none;
    flex: 1;
    min-width: 0;
    cursor: text;

    &::placeholder {
      color: #D4D4D8;
    }

    &:hover {
      border: none !important;
    }
  }

  &__value {
    flex: 1;
    min-width: 0;
    color: #09090B;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--placeholder {
      color: #D4D4D8;
    }
  }

  &__icon {
    margin-left: 8px;
    transition: transform 0.2s;
    flex-shrink: 0;
    will-change: transform;
  }

  &__clear-icon {
    margin-left: 8px;
    flex-shrink: 0;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.6;
    }
  }

  &--open &__icon {
    transform: rotate(180deg);
  }

  &__loader {
    width: 20px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__dropdown {
    position: absolute;
    max-height: 300px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    width: 100%;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    left: 0;

    &--bottom {
      top: calc(100% + 1px);
    }

    &--top {
      bottom: calc(100% + 5px);
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f3f4f6;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &__option {
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: #09090B;

    &:hover,
    &.is-focused {
      background-color: #f3f4f6;
    }

    &.is-selected {
      background-color: #eff6ff;
      color: #FB923C;
      font-weight: 500;
    }
  }

  &__empty {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  }

  &--error {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #f00;
    margin-top: 4px;
    margin-bottom: 12px;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
